import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Preloader from '../components/Preloader';
import Footer from '../components/Footer';
import { FaArrowLeft } from 'react-icons/fa';
import ScrollReveal from '../components/ScrollReveal';

const guides = [
    {
        id: 1,
        title: 'A Journey Through Accra: The Heartbeat of Ghana',
        category: 'Ghana',
        description: 'Explore the bustling Makola Market, the historic Jamestown, and the serene beaches of Labadi in this comprehensive virtual guide to Ghana\'s capital.',
        image: '/assets/ariellandscape.jpg',
        fullContent: 'Accra, the vibrant capital of Ghana, is a city where tradition and modernity dance together in perfect harmony. From the bustling markets to the serene coastline, this city offers an unforgettable experience. Explore the historic Jamestown district with its colonial architecture, visit the famous Makola Market where locals trade fresh produce and goods, and relax on the beautiful Labadi Beach where the Atlantic Ocean meets the shore.',
        images: ['/assets/ariellandscape.jpg', '/assets/ariellandscape.jpg', '/assets/ariellandscape.jpg'],
    },
    {
        id: 2,
        title: 'Senegal\'s Gorée Island: A Site of Remembrance',
        category: 'West Africa',
        description: 'Visit the historic "House of Slaves" and its "Door of No Return," a powerful memorial to the victims of the slave trade.',
        image: '/assets/kumasiarielview.jpeg',
        fullContent: 'Gorée Island stands as a poignant reminder of Africa\'s difficult history. This UNESCO World Heritage Site was once a major hub for the slave trade, and today it serves as a memorial to those who suffered. Walk through the narrow streets lined with colorful colonial buildings, visit the haunting House of Slaves museum, and stand at the Door of No Return—a powerful symbol of the transatlantic slave trade. The island is both a place of pain and hope, drawing visitors from around the world.',
        images: ['/assets/kumasiarielview.jpeg', '/assets/kumasiarielview.jpeg', '/assets/kumasiarielview.jpeg'],
    },
    {
        id: 3,
        title: 'Serengeti National Park, Tanzania',
        category: 'Africa',
        description: 'Witness the Great Migration, where millions of wildebeest and zebra traverse the plains in a spectacular display of nature.',
        image: '/assets/kakumnationalpark1.jpeg',
        fullContent: 'The Serengeti is one of the world\'s most spectacular natural wonders, home to the Great Migration. Each year, over two million wildebeest, zebras, and other herbivores embark on an incredible journey across the African plains, creating one of nature\'s most dramatic events. Beyond the migration, the Serengeti is home to the "Big Five" and countless other wildlife species. Experience the raw beauty of Africa, watch predators and prey interact in their natural habitat, and witness sunsets that paint the sky in breathtaking colors.',
        images: ['/assets/kakumnationalpark1.jpeg', '/assets/kakumnationalpark1.jpeg', '/assets/kakumnationalpark1.jpeg'],
    },
];

const GuideDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const guide = guides.find(g => g.id === parseInt(id));

    if (!guide) {
        return (
            <div className='font-nixie-one bg-gray-50 min-h-screen text-gray-800'>
                <PageHeader />
                <Preloader />
                <main className="flex items-center justify-center min-h-screen">
                    <div className="text-center">
                        <h2 className="text-4xl font-bold mb-4">Guide Not Found</h2>
                        <button onClick={() => navigate('/travel-guides')} className="px-6 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors duration-300">
                            Back to Travel Guides
                        </button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className='font-nixie-one bg-gray-50 min-h-screen text-gray-800'>
            <PageHeader />
            <Preloader />
            <main>
                {/* Back Button */}
                <section className="bg-white py-6 px-4 border-b">
                    <div className="container mx-auto">
                        <button onClick={() => navigate('/travel-guides')} className="flex items-center gap-2 text-green-600 hover:text-green-700 font-semibold transition-colors duration-300">
                            <FaArrowLeft /> Back to Travel Guides
                        </button>
                    </div>
                </section>

                {/* Hero Section with Main Image */}
                <section className="relative h-96 bg-cover bg-center">
                    <img src={guide.image} alt={guide.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40"></div>
                    <div className="absolute inset-0 flex items-end justify-start">
                        <div className="p-8 text-white">
                            <span className="inline-block bg-green-600 text-white text-xs font-bold px-3 py-1 rounded mb-4">{guide.category}</span>
                            <h1 className="text-4xl md:text-6xl font-cormorant-upright font-bold">{guide.title}</h1>
                        </div>
                    </div>
                </section>

                {/* Content Section */}
                <section className="py-16 px-4">
                    <div className="container mx-auto max-w-4xl">
                        <ScrollReveal animationClass="animate-fade-in" threshold={0.1}>
                            <article className="bg-white rounded-lg shadow-lg p-8">
                                <h2 className="text-2xl font-cormorant-upright font-bold mb-6">Guide Overview</h2>
                                <p className="text-gray-700 text-lg leading-relaxed mb-8">{guide.fullContent}</p>

                                {/* Image Gallery */}
                                <div className="mb-8">
                                    <h3 className="text-2xl font-cormorant-upright font-bold mb-6">Photo Gallery</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        {guide.images.map((image, index) => (
                                            <div key={index} className="rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
                                                <img 
                                                    src={image} 
                                                    alt={`${guide.title} ${index + 1}`} 
                                                    className="w-full h-48 object-cover transform hover:scale-105 transition-transform duration-500"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Additional Info */}
                                <div className="border-t pt-8">
                                    <h3 className="text-2xl font-cormorant-upright font-bold mb-4">Essential Information</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="bg-gray-50 p-4 rounded-lg">
                                            <h4 className="font-bold text-green-600 mb-2">Category</h4>
                                            <p className="text-gray-700">{guide.category}</p>
                                        </div>
                                        <div className="bg-gray-50 p-4 rounded-lg">
                                            <h4 className="font-bold text-green-600 mb-2">Best For</h4>
                                            <p className="text-gray-700">Cultural Exploration & Historical Sites</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Call to Action */}
                                <div className="mt-8 bg-green-50 border-l-4 border-green-600 p-6 rounded">
                                    <h4 className="font-bold text-lg mb-2">Ready to Explore?</h4>
                                    <p className="text-gray-700 mb-4">Plan your next adventure by visiting this incredible destination. Click the button below to start your virtual exploration.</p>
                                    <button className="px-6 py-2 text-sm bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 transition-colors duration-300">
                                        <a href="/virtual-tour" rel="noreferrer">Start Virtual Tour</a>
                                    </button>
                                </div>
                            </article>
                        </ScrollReveal>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default GuideDetail;
